import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { RootState, useAppDispatch } from '../store';
import { checkOrganizationDomain } from '../store/actions/OnboardingActions';
import apis from '../utils/apis';
import { getAuth0Email } from '../auth0-integration/utils/auth0EmailUtils';
import UserOnboardingStep from '../components/Onboarding/UserOnboardingStep';
import PreferencesSetStep from '../components/Onboarding/PreferencesSetStep';
import LoadingScreen from '../auth0-integration/components/LoadingScreen';
import OrganizationCheckStep from '../components/Onboarding/OrganizationCheckStep';
import WelcomeStep from '../components/Onboarding/WelcomeStep';

const OnboardingPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { isAuthenticated, user: auth0User } = useAuth0();
  const onboarding = useSelector((state: RootState) => state.onboarding);
  const isUserLoaded = useSelector((state: RootState) => !!state.user?.userInfo?.UserID);
  const orgCheckTriggeredRef = useRef(false);
  const profileFetchTriggeredRef = useRef(false);

  const realEmail = getAuth0Email(auth0User);
  const logoUrl = '/logosvg.svg';

  // Fetch user's profile on mount to check organization_id status
  // IMPORTANT: Only call after useAuth0Profile has completed /auth/login (check userInfo exists)
  useEffect(() => {
    if (!profileFetchTriggeredRef.current && isAuthenticated && isUserLoaded && !onboarding.user) {
      profileFetchTriggeredRef.current = true;

      const fetchUserProfile = async () => {
        try {
          const response = await apis.get('/onboarding/get-user-info');
          if (response.data.success && response.data.user) {
            dispatch({
              type: 'ONBOARDING_GET_USER_INFO_SUCCESS',
              payload: response.data,
            });
          }
        } catch (error) {
          // Silently handle error
        }
      };

      fetchUserProfile();
    }
  }, [isAuthenticated, isUserLoaded, dispatch, onboarding.user]);

  // Trigger organization domain check ONLY when on organization step with no org selected
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (
      !orgCheckTriggeredRef.current &&
      isAuthenticated &&
      realEmail &&
      onboarding.currentStep === 'organization' &&
      !onboarding.selectedOrganization &&
      onboarding.user !== null &&
      !onboarding.user?.organization_id &&
      !onboarding.isLoading &&
      onboarding.matchingOrganizations.length === 0
    ) {
      orgCheckTriggeredRef.current = true;
      dispatch(checkOrganizationDomain(realEmail) as any);
    }
  }, [
    isAuthenticated,
    realEmail,
    dispatch,
    onboarding.currentStep,
    onboarding.selectedOrganization,
    onboarding.user?.organization_id,
    onboarding.user,
    onboarding.isLoading,
    onboarding.matchingOrganizations.length,
  ]);

  // Show loading screen while initial user data is being fetched or if step is not set
  if (!isAuthenticated) {
    return <LoadingScreen />;
  }

  if (!onboarding.user || onboarding.currentStep === null) {
    return <LoadingScreen />;
  }

  const isCenterStep = onboarding.currentStep === 'welcome' || onboarding.currentStep === 'organization';

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-secondary-50 dark:from-gray-900 dark:to-gray-800 flex flex-col">
      {/* Step Indicator - Static at top, hidden on welcome step */}
      {onboarding.currentStep !== 'welcome' && (
        <div className="bg-white dark:bg-gray-800/80 border-b border-gray-200 dark:border-gray-700 relative z-40 backdrop-blur-sm">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <div className="flex items-center justify-center gap-1 sm:gap-2">
              {/* Step 1: Organization */}
              <div className="text-center flex-1 relative">
                <div
                  className={`text-base sm:text-lg font-bold transition-colors ${
                    onboarding.currentStep === 'organization' || onboarding.user?.organization_id
                      ? 'text-black dark:text-white'
                      : 'text-gray-400 dark:text-gray-600'
                  }`}
                >
                  1
                </div>
                <div className={`text-xs mt-0.5 transition-colors ${
                  onboarding.currentStep === 'organization' || onboarding.user?.organization_id
                    ? 'text-black dark:text-white font-semibold'
                    : 'text-gray-600 dark:text-gray-400'
                }`}>{t('Organization')}</div>
              </div>

              {/* Connector */}
              <div
                className={`flex-1 h-px transition-colors ${
                  onboarding.user?.organization_id
                    ? 'bg-primary-600 dark:bg-primary-500'
                    : 'bg-gray-500 dark:bg-gray-300'
                }`}
              ></div>

              {/* Step 2: Profile */}
              <div className="text-center flex-1 relative">
                <div
                  className={`text-base sm:text-lg font-bold transition-colors ${
                    onboarding.currentStep === 'user'
                      ? 'text-black dark:text-white'
                      : onboarding.currentStep === 'organization'
                      ? 'text-gray-400 dark:text-gray-600'
                      : onboarding.user?.organization_id && onboarding.user?.first_name
                      ? 'text-primary-900 dark:text-primary-100'
                      : 'text-gray-400 dark:text-gray-600'
                  }`}
                >
                  2
                </div>
                <div className={`text-xs mt-0.5 transition-colors ${
                  onboarding.currentStep === 'user' || (onboarding.user?.organization_id && onboarding.user?.first_name)
                    ? 'text-black dark:text-white font-semibold'
                    : 'text-gray-400 dark:text-gray-600'
                }`}>{t('Profile')}</div>
              </div>

              {/* Connector */}
              <div
                className={`flex-1 h-px transition-colors ${
                  onboarding.user?.organization_id && onboarding.user?.first_name
                    ? 'bg-primary-600 dark:bg-primary-500'
                    : 'bg-gray-500 dark:bg-gray-300'
                }`}
              ></div>

              {/* Step 3: Preferences */}
              <div className="text-center flex-1 relative">
                <div
                  className={`text-base sm:text-lg font-bold transition-colors ${
                    onboarding.currentStep === 'preferences'
                      ? 'text-black dark:text-white'
                      : onboarding.currentStep === 'user'
                      ? 'text-gray-400 dark:text-gray-600'
                      : onboarding.isComplete
                      ? 'text-primary-900 dark:text-primary-100'
                      : 'text-gray-400 dark:text-gray-600'
                  }`}
                >
                  3
                </div>
                <div className={`text-xs mt-0.5 transition-colors ${
                  onboarding.currentStep === 'preferences'
                    ? 'text-black dark:text-white font-semibold'
                    : 'text-gray-600 dark:text-gray-400'
                }`}>{t('Preferences')}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content Area - Flows below progress bar */}
      <div className="flex-1 w-full overflow-y-auto custom-scrollbar-main max-h-[calc(100vh-120px)]">
        {onboarding.isLoading && (
          <div className="flex items-center justify-center w-full h-full min-h-screen">
            <div className="text-center">
              <div className="flex justify-center mb-3">
                <img
                  src={logoUrl}
                  alt="PONS Logo"
                  className="w-10 h-10 animate-spin"
                />
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {onboarding.currentStep === 'user' ? t('Loading profile and role...') : t('Setting up your account...')}
              </p>
            </div>
          </div>
        )}

        {!onboarding.isLoading && isCenterStep && (
          <div className="w-full flex items-center justify-center min-h-screen px-4">
            {onboarding.currentStep === 'welcome' && <WelcomeStep />}
            {onboarding.currentStep === 'organization' && <OrganizationCheckStep />}
          </div>
        )}

        {!onboarding.isLoading && !isCenterStep && (
          <div className="max-w-3xl mx-auto px-4 py-8 pb-4 w-full">
            <div className="space-y-6">
              {/* User Details Step - Only available if org_id exists */}
              {onboarding.currentStep === 'user' && onboarding.user?.organization_id && <UserOnboardingStep />}

              {/* Preferences Step - Only available if org_id exists and user details are filled */}
              {onboarding.currentStep === 'preferences' &&
                onboarding.user?.organization_id &&
                onboarding.user?.first_name && <PreferencesSetStep />}

              {/* Error Display */}
              {onboarding.error && (
                <div className="rounded-lg bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 p-4">
                  <p className="text-sm text-danger-700 dark:text-danger-400">{onboarding.error}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OnboardingPage;
